import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { metaTags } from '@app/meta-tags';
import { LOCALE_IDS } from '@constants/app.constant';
import { SearchStep } from '@constants/flight.constant';
import { authGuard, checkAuthGuard } from '@core/guards/auth.guard';
import { dirtyCheckGuard } from '@core/guards/dirty-check.guard';
import { linkGuard } from '@core/guards/link.guard';
import { environment } from '@env/environment';
import { PageComponent } from '@pages/page.component';
import { AccountOrderComponent } from '@pages/personal-account/account-orders/account-order/account-order.component';
import { accountOrderResolver } from '@resolvers/account-order.resolver';
import { customerInfoResolver } from '@resolvers/customer-info.resolver';
import { flightResolver } from '@resolvers/flight.resolver';
import { getLinkResolver } from '@resolvers/get-link.resolver';
import { homeResolver } from '@resolvers/home.resolver';
import { paymentResolver } from '@resolvers/payment.resolver';

export function langMatcher(segments: UrlSegment[]): UrlMatchResult | null {
    const supportedLangs = [LOCALE_IDS.VI, LOCALE_IDS.EN];
    const [first] = segments;
    const { options } = environment;

    if (first && supportedLangs.includes(first.path) && options.isSSRMode) {
        return {
            consumed: [first],
            posParams: { lang: first },
        };
    }

    return { consumed: [] };
}

export const routes: Routes = [
    {
        matcher: langMatcher,
        component: PageComponent,
        canActivate: [checkAuthGuard],
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('@pages/home/home.component').then((v) => v.HomeComponent),
                data: {
                    metaTags: metaTags.home,
                },
                resolve: {
                    home: homeResolver,
                },
            },
            {
                path: 'flight',
                loadComponent: () =>
                    import('@pages/flight/flight.component').then((v) => v.FlightComponent),
                data: {
                    metaTags: metaTags.flight,
                },
            },
            {
                path: 'flight/search',
                loadComponent: () =>
                    import('@pages/flight/search/search.component').then((v) => v.SearchComponent),
                children: [
                    {
                        path: 'full-search',
                        loadComponent: () =>
                            import(
                                '@pages/flight/search/select-flight/select-flight.component'
                            ).then((v) => v.SelectFlightComponent),
                        resolve: {
                            verify: flightResolver,
                        },
                        title: $localize`:@@common.meta-tags.home.title:Mua vé máy bay giá rẻ - Đặt vé nhanh chóng & tiện lợi`,
                        data: {
                            step: SearchStep.SELECT_FLIGHT,
                            metaTags: metaTags.searchFlight,
                            idleCheck: true,
                            store: true,
                            storeWhenNavigate: ['customer-info'],
                        },
                        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                    },
                    {
                        path: 'customer-info',
                        canDeactivate: [dirtyCheckGuard],
                        loadComponent: () =>
                            import(
                                '@pages/flight/search/customer-info/customer-info.component'
                            ).then((v) => v.CustomerInfoComponent),
                        resolve: {
                            selected: customerInfoResolver,
                        },
                        data: {
                            step: SearchStep.CUSTOMER_INFO,
                            metaTags: metaTags.customerInfo,
                            idleCheck: true,
                        },
                        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                    },
                    {
                        path: 'payment/:id',
                        loadComponent: () =>
                            import('@pages/flight/search/payment/payment.component').then(
                                (v) => v.PaymentComponent
                            ),
                        resolve: {
                            detail: paymentResolver,
                        },
                        data: {
                            step: SearchStep.PAYMENT,
                            metaTags: metaTags.payment,
                            idleCheck: true,
                        },
                        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                    },
                    {
                        path: 'order-result/:id',
                        loadComponent: () =>
                            import('@flight-page/search/order-result/order-result.component').then(
                                (v) => v.OrderResultComponent
                            ),
                        resolve: {
                            detail: paymentResolver,
                        },
                        data: {
                            step: SearchStep.ORDER_RESULT,
                            metaTags: metaTags.orderResult,
                        },
                        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                    },
                    { path: '**', redirectTo: '/flight' },
                    { path: '', redirectTo: '/flight', pathMatch: 'full' },
                ],
            },
            {
                path: 'flight/share',
                loadComponent: () =>
                    import('@pages/flight/share-flight/share-flight.component').then(
                        (v) => v.ShareFlightComponent
                    ),
                data: {
                    metaTags: metaTags.shareFlight,
                },
            },
            {
                path: 'promotion',
                loadComponent: () =>
                    import('@pages/promotion/promotion-list/promotion-list.component').then(
                        (v) => v.PromotionListComponent
                    ),
                data: {
                    metaTags: metaTags.promotion,
                },
            },
            {
                path: 'promotion/details/:id',
                loadComponent: () =>
                    import('@pages/promotion/promotion-details/promotion-details.component').then(
                        (v) => v.PromotionDetailsComponent
                    ),
                data: {
                    metaTags: metaTags.promotionDetail,
                },
            },
            {
                path: 'search-order',
                loadComponent: () =>
                    import('@pages/search-order/search-order.component').then(
                        (v) => v.SearchOrderComponent
                    ),
                data: {
                    metaTags: metaTags.searchOrder,
                },
            },
            {
                path: 'search-order/:orderId',
                component: AccountOrderComponent,
                resolve: {
                    detail: accountOrderResolver,
                },
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import(
                                '@pages/personal-account/account-orders/account-order/order-detail/order-detail.component'
                            ).then((v) => v.OrderDetailComponent),
                        data: { routeName: 'search-order', metaTags: metaTags.searchOrderDetail },
                    },
                    {
                        path: 'flight',
                        loadComponent: () =>
                            import(
                                '@pages/personal-account/account-orders/account-order/order-detail-flight/order-detail-flight.component'
                            ).then((v) => v.OrderDetailFlightComponent),
                        data: { routeName: 'search-order' },
                    },
                ],
            },
            {
                path: 'account',
                loadComponent: () =>
                    import('@pages/personal-account/personal-account.component').then(
                        (v) => v.PersonalAccountComponent
                    ),
                canActivate: [authGuard],
                children: [
                    {
                        path: 'information',
                        data: {
                            metaTags: metaTags.accountInformation,
                        },
                        loadComponent: () =>
                            import(
                                '@pages/personal-account/personal-account-information/personal-account-information.component'
                            ).then((v) => v.PersonalAccountInformationComponent),
                    },
                    {
                        path: 'orders',
                        data: {
                            metaTags: metaTags.accountOrders,
                        },
                        loadComponent: () =>
                            import(
                                '@pages/personal-account/account-orders/account-orders.component'
                            ).then((v) => v.AccountOrdersComponent),
                    },
                    {
                        path: 'orders/:orderId',
                        component: AccountOrderComponent,
                        resolve: {
                            detail: accountOrderResolver,
                        },
                        children: [
                            {
                                path: '',

                                loadComponent: () =>
                                    import(
                                        '@pages/personal-account/account-orders/account-order/order-detail/order-detail.component'
                                    ).then((v) => v.OrderDetailComponent),
                                data: {
                                    routeName: 'account-order',
                                    metaTags: metaTags.accountOrders,
                                },
                            },
                            {
                                path: 'flight',
                                loadComponent: () =>
                                    import(
                                        '@pages/personal-account/account-orders/account-order/order-detail-flight/order-detail-flight.component'
                                    ).then((v) => v.OrderDetailFlightComponent),
                                data: { routeName: 'account-order' },
                            },
                        ],
                    },
                ],
                data: {
                    authCheck: true,
                },
            },
            {
                path: 'contact-us',
                loadComponent: () =>
                    import('@pages/contact-us/contact-us.component').then(
                        (v) => v.ContactUsComponent
                    ),
                data: {
                    metaTags: metaTags.contactUs,
                },
            },
            {
                path: 'new-agency',
                loadComponent: () =>
                    import('@pages/new-agency/new-agency.component').then((v) => v.NewAgencyComponent),
                data: {
                    metaTags: metaTags.newAgency,
                },
            },
            // {
            //     path: 'news',
            //     loadComponent: () =>
            //         import('@pages/news/news-list/news-list.component').then(
            //             (v) => v.NewsListComponent
            //         ),
            // },
            // {
            //     path: 'news/more/:id',
            //     loadComponent: () =>
            //         import('@pages/news/news-more/news-more.component').then(
            //             (v) => v.NewsMoreComponent
            //         ),
            // },
            // {
            //     path: 'news/details/:id',
            //     loadComponent: () =>
            //         import('@pages/news/news-detail/news-detail.component').then(
            //             (v) => v.NewsDetailComponent
            //         ),
            // },
            {
                path: 'about-us',
                loadComponent: () =>
                    import('@pages/about-us/about-us.component').then((v) => v.AboutUsComponent),
                data: {
                    metaTags: metaTags.aboutUs,
                },
            },
            {
                path: 'policy',
                loadComponent: () =>
                    import('@pages/policy/policy.component').then((v) => v.PolicyComponent),
                data: {
                    metaTags: metaTags.policy,
                },
            },
            {
                path: 'terms-and-conditions',
                loadComponent: () =>
                    import('@pages/terms-and-conditions/terms-and-conditions.component').then(
                        (v) => v.TermsAndConditionsComponent
                    ),
                data: {
                    metaTags: metaTags.termsAndConditions,
                },
            },
            {
                path: 'how-to-book',
                loadComponent: () =>
                    import(
                        '@pages/booking-ticket-instruction/booking-ticket-instruction.component'
                    ).then((v) => v.BookingTicketInstructionComponent),
                data: {
                    metaTags: metaTags.howToBook,
                },
            },
            {
                path: 'how-to-pay',
                loadComponent: () =>
                    import('@pages/payment-instruction/payment-instruction.component').then(
                        (v) => v.PaymentInstructionComponent
                    ),
                data: {
                    metaTags: metaTags.howToPay,
                },
            },
            {
                path: 'auth',
                loadChildren: () => import('@pages/auth/auth.module').then((m) => m.AuthModule),
            },
            {
                path: ':id',
                canMatch: [linkGuard],
                loadComponent: () =>
                    import('@pages/get-link/get-link.component').then((v) => v.GetLinkComponent),
                data: {
                    metaTags: metaTags.link,
                },
                resolve: {
                    detail: getLinkResolver,
                },
            },
            {
                path: 'error/access-denied',
                loadComponent: () =>
                    import('@pages/errors/access-denied/access-denied.component').then(
                        (v) => v.AccessDeniedComponent
                    ),
                data: {
                    metaTags: metaTags.accessDenied,
                },
            },
            {
                path: 'error/not-found',
                loadComponent: () =>
                    import('@pages/errors/not-found/not-found.component').then(
                        (v) => v.NotFoundComponent
                    ),
                data: {
                    metaTags: metaTags.notFound,
                },
            },
        ],
    },
    { path: '**', redirectTo: '/error/not-found' },
    { path: '', redirectTo: '/', pathMatch: 'full' },
];
